import { toValue, watch, type MaybeRefOrGetter } from 'vue';
import { useHead, useRoute, useState } from '#imports';

export type FaqItem = {
  question: string;
  answer: string;
};

export function useFaqSchema(faqsInput?: MaybeRefOrGetter<FaqItem[] | null | undefined>) {
  const route = useRoute();
  const isSchemaAttached = useState<boolean>(`faq-schema-${route.path}`, () => false);

  watch(
    () => toValue(faqsInput),
    (faqs) => {
      if (isSchemaAttached.value) {
        return;
      }

      const sanitizedFaqs = Array.isArray(faqs)
        ? faqs
            .filter((faq) => faq?.question && faq?.answer)
            .map((faq) => ({
              question: faq.question,
              answer: faq.answer,
            }))
        : [];

      if (sanitizedFaqs.length === 0) {
        return;
      }

      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: sanitizedFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      };

      useHead(() => ({
        script: [
          {
            key: 'faq-schema',
            type: 'application/ld+json',
            children: JSON.stringify(faqSchema),
          },
        ],
      }));

      isSchemaAttached.value = true;
    },
    { immediate: true, deep: true },
  );
}
